/**
 * Замок перед приложением.
 *
 * Оболочка вокруг всего: пока заперто, приложения не видно вовсе — ни списка
 * дел, ни таблетницы. Это и есть смысл замка, и потому он живёт выше
 * маршрутизатора, а не внутри экранов.
 *
 * ## Что замок делает и чего не делает
 *
 * Он закрывает обычный путь: взяли чужой телефон, открыли приложение. Он не
 * шифрует данные — записи лежат в базе как лежали, и человек, который откроет
 * её напрямую, прочитает всё, не касаясь кода. Врать об этом в настройках
 * нельзя, поэтому там сказано прямо.
 */

import { useEffect, type ReactNode, useState } from 'react';

import { getAuthProvider } from '@/features/auth/api/auth-provider';
import { useAuthStore } from '@/features/auth/model/auth-store';
import { toast } from '@/shared/ui';

import { systemBiometrics } from '../model/biometrics';
import { clearLock, readLockSettings } from '../model/lock-settings';
import { useLockStore } from '../model/lock-store';

import { ForgotPinSheet } from './ForgotPinSheet';
import { LockScreen } from './LockScreen';

export function AppLockGate({ children }: { children: ReactNode }) {
  const user = useAuthStore((state) => state.user);
  const status = useAuthStore((state) => state.status);

  const locked = useLockStore((state) => state.locked);
  const attempts = useLockStore((state) => state.attempts);
  const init = useLockStore((state) => state.init);
  const unlock = useLockStore((state) => state.unlock);
  const open = useLockStore((state) => state.open);
  const visibility = useLockStore((state) => state.visibility);

  const [biometricsEnabled, setBiometricsEnabled] = useState(true);
  const [forgot, setForgot] = useState(false);

  const userId = user?.id ?? null;

  useEffect(() => {
    if (status !== 'authenticated' || !userId) return;

    void init(userId);
    void readLockSettings(userId).then((settings) => setBiometricsEnabled(settings.biometrics));
  }, [status, userId, init]);

  /*
    Запирание при возвращении. Слушаем видимость вкладки: она меняется и когда
    приложение свернули, и когда переключились на другое — то есть ровно в те
    моменты, после которых телефон может оказаться в чужих руках.
  */
  useEffect(() => {
    if (!userId) return;

    const onChange = () => void visibility(document.visibilityState === 'visible', userId);
    document.addEventListener('visibilitychange', onChange);

    return () => document.removeEventListener('visibilitychange', onChange);
  }, [userId, visibility]);

  // До входа в аккаунт замка нет: там своя дверь.
  if (status !== 'authenticated' || !userId || locked !== true) return <>{children}</>;

  const openByPassword = async (password: string): Promise<boolean> => {
    if (!user) return false;

    try {
      await getAuthProvider().login({ email: user.email, password });
    } catch {
      return false;
    }

    await clearLock(userId);
    setForgot(false);
    open();
    toast.show('Пароль убран — новый можно задать в настройках');

    return true;
  };

  return (
    <>
      <LockScreen
        attempts={attempts}
        biometricsEnabled={biometricsEnabled}
        onSubmit={(pin) => unlock(userId, pin)}
        onBiometrics={async () => {
          const ok = await systemBiometrics().authenticate();
          if (ok) open();
          return ok;
        }}
        onForgot={() => setForgot(true)}
      />

      {forgot && user && (
        <ForgotPinSheet
          email={user.email}
          onClose={() => setForgot(false)}
          onSubmit={openByPassword}
        />
      )}
    </>
  );
}
